import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Link,
  Stack,
  Grid,
  TextField,
  IconButton,
} from "@mui/material";
import InstagramIcon from "@mui/icons-material/Instagram";
import TwitterIcon from "@mui/icons-material/Twitter";
import FacebookIcon from "@mui/icons-material/Facebook";
import { YouTube } from "@mui/icons-material";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import YouTubeIcon from "@mui/icons-material/YouTube";
import { FaTiktok, FaSnapchat, FaImdb } from "react-icons/fa";
import RichTextEditor from "./RichTextEditor";
import api from "../api/api";
import { ENDPOINTS } from "../api/endpoints";

const ALLOWED_FILE_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "video/mp4",
  "video/quicktime",
  "image/gif",
  "audio/mpeg",
];
const MAX_MESSAGE_LENGTH = 2300;

const sanitizePhone = (value = "") => String(value).replace(/[^\d+]/g, "");
const normalizeUrl = (url = "") =>
  url.startsWith("http") ? url : `https://${url}`;

export default function MessagePrompt({
  open,
  onClose,
  onSent,
  contact = {},
  headings = [],
  representationInfoList = {},
  talentId,
  defaultSubject = "",
  editorHeight = "300",
  requireSenderEmail = false,
  senderEmail = "",
}) {
  const fromEmailRaw = localStorage.getItem("email") || senderEmail || "";
  const userRoleRaw = localStorage.getItem("userRole");
  const messageFrom =
    userRoleRaw === "COMPANY"
      ? "Director"
      : userRoleRaw === "USER"
        ? "Talent"
        : null;

  const [subject, setSubject] = useState(defaultSubject);
  const [body, setBody] = useState("");
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [youTubeURL, setYouTubeURL] = useState("");
  const [showYouTube, setShowYouTube] = useState(false);
  const [sending, setSending] = useState(false);
  const [reps, setReps] = useState({});
  const [userId, setUserId] = useState(null);
  const [senderEmailInput, setSenderEmailInput] = useState(senderEmail || "");
  const [senderEmailError, setSenderEmailError] = useState("");

  const resolvedFromEmail = requireSenderEmail
    ? senderEmailInput
    : fromEmailRaw;

  /* CONTACT MAPPING — matches ContactDialog pattern */
  const mappedContact = useMemo(
    () => ({
      phone: contact.phone || contact.primaryContactNumber || "",

      secondary:
        contact.secondaryPhone ||
        contact.secondaryContactNumber ||
        contact.secondary ||
        "",

      email: contact.email || "",

      faceBook: contact.faceBook || contact.facebook || "",

      twitter: contact.twitter || "",

      instagram: contact.instagram || "",

      tikTok: contact.tikTok || contact.tiktok || "",

      personalWebSite: contact.personalWebSite || contact.website || "",

      imdb: contact.imdb || contact.IMDb || "",

      youTube: contact.youTube || contact.youtube || contact.youtubeURL || "",

      snapChat: contact.snapChat || contact.snapchat || "",

      paymentLink1: contact.paymentLink1 || "",
      paymentLink2: contact.paymentLink2 || "",
      paymentLink3: contact.paymentLink3 || "",

      name:
        contact.name ||
        `${contact.firstName || ""} ${contact.lastName || ""}`.trim(),
    }),
    [contact],
  );
  const paymentLinks = [1, 2, 3]
    .map((i) => contact[`paymentLink${i}`] || contact[`paymentlink${i}`])
    .filter(Boolean);

  const socialItems = [
    {
      icon: <InstagramIcon fontSize="small" />,
      value: mappedContact.instagram,
    },
    {
      icon: <TwitterIcon fontSize="small" />,
      value: mappedContact.twitter,
    },
    {
      icon: <FacebookIcon fontSize="small" />,
      value: mappedContact.faceBook,
    },
    {
      icon: <YouTube fontSize="small" />,
      value: mappedContact.youTube,
    },
    {
      icon: <FaTiktok size={18} />,
      value: mappedContact.tikTok,
    },
    {
      icon: <FaImdb size={18} />,
      value: mappedContact.imdb,
    },
    {
      icon: <FaSnapchat size={18} />,
      value: mappedContact.snapChat,
    },
  ].filter((item) => item.value);

  const hasPersonalContact =
    mappedContact.phone ||
    mappedContact.secondary ||
    mappedContact.email ||
    mappedContact.personalWebSite;

  /* USER ID */
  useEffect(() => {
    const id = localStorage.getItem("userId");
    if (id && id !== "undefined") setUserId(id);
  }, []);

  /* REPRESENTATION — matches ContactDialog pattern */
  useEffect(() => {
    const formatted = {};
    headings.forEach((h) => {
      const infoArr = representationInfoList[h] || [];
      const info = infoArr[0] || {};
      formatted[h] = {
        nameofCompany: info.nameofCompany || "",
        representativeName: info.representativeName || "",
        representativeContact: info.representativeContact || "",
      };
    });
    setReps(formatted);
  }, [headings, representationInfoList]);

  const repEntries = headings
    .map((h) => reps[h])
    .filter(
      (info) => info && (info.nameofCompany || info.representativeContact),
    );

  /* Reset on open */
  useEffect(() => {
    if (open) {
      setSenderEmailInput(senderEmail || "");
      setSenderEmailError("");
    }
  }, [open, senderEmail]);

  /* FILE */
  const handleFileSelect = (e) => {
    const f = e.target.files[0];
    if (!f) return;

    if (!ALLOWED_FILE_TYPES.includes(f.type)) {
      setFileError("Invalid file type.");
      setFile(null);
    } else {
      setFileError("");
      setFile(f);

      // Clear YouTube
      setYouTubeURL("");
      setShowYouTube(false);
    }

    e.target.value = "";
  };

  const handleFileRemove = () => {
    setFile(null);
    setFileError("");
  };
  const isMessageEmpty = (html = "") => {
    const text = html
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, " ")
      .trim();
    return text.length === 0;
  };
  /* SEND */
  const handleSend = async () => {
    const hasMessage = !isMessageEmpty(body);
    const hasVideo = showYouTube && youTubeURL.trim();

    if (!hasMessage && !hasVideo) {
      return;
    }

    if (requireSenderEmail) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!senderEmailInput.trim()) {
        setSenderEmailError("Your email is required to send a message.");
        return;
      }
      if (!emailRegex.test(senderEmailInput.trim())) {
        setSenderEmailError("Please enter a valid email address.");
        return;
      }
    }

    setSending(true);
    try {
      const form = new FormData();
      const hasMessage = !isMessageEmpty(body);

      let finalMessage = hasMessage ? body : "";

      // Guest users
      if (messageFrom === null) {
        finalMessage = `${finalMessage}

If you want to respond please reply to this email id: ${resolvedFromEmail}`;
      }

      form.append("MessageText", finalMessage);
      form.append("FromEmail", resolvedFromEmail);
      form.append("NoticeId", "0");
      form.append("ToEmail", mappedContact.email);
      form.append("RecipientName", mappedContact.name);
      form.append("TalentId", String(talentId));
      form.append("MessageSubject", subject.trim() || "Direct message");
      form.append("ParentId", "0");
      form.append("UserEmail", resolvedFromEmail);
      form.append("CanReply", messageFrom === null ? "false" : "true");
      if (messageFrom !== null) {
        form.append("MessageFrom", messageFrom);
      }
      if (file) {
        form.append("File", file);
      }

      // External video URL (YouTube/Vimeo/etc)
      if (showYouTube && youTubeURL.trim()) {
        form.append("FileURL", youTubeURL.trim());
      }
      await api.post(ENDPOINTS.sendMessage(), form);
      onSent?.();
      setSubject(defaultSubject);
      setBody("");
      setFile(null);
      setYouTubeURL("");
      setShowYouTube(false);
      setSenderEmailInput("");
      setSenderEmailError("");
      onClose();
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Send Message
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Sender email — only for non-logged-in visitors */}
        {requireSenderEmail && (
          <TextField
            fullWidth
            label="Your Email"
            margin="dense"
            type="email"
            required
            value={senderEmailInput}
            onChange={(e) => {
              setSenderEmailInput(e.target.value);
              setSenderEmailError("");
            }}
            error={!!senderEmailError}
            helperText={
              senderEmailError ||
              "We'll use this so the talent can reply to you"
            }
          />
        )}

        <TextField
          fullWidth
          label="Subject"
          margin="dense"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
        />

        <Box sx={{ mt: 2 }}>
          <RichTextEditor
            value={body}
            onChange={setBody}
            maxLength={MAX_MESSAGE_LENGTH}
            editorStyle={{ height: `${editorHeight}px` }}
          />
        </Box>

        {/* FILE + YOUTUBE */}
        <Box sx={{ display: "flex", pt: 2 }}>
          <Box sx={{ mt: 2, display: "flex", alignItems: "center" }}>
            <Button
              component="label"
              startIcon={<AttachFileIcon />}
              sx={{ mr: 2 }}
              disabled={!!youTubeURL}
            >
              Attach File
              <input
                type="file"
                hidden
                accept=".pdf,.jpg,.jpeg,.png,.mp4,.mov,.gif,.mp3,.doc,.docx"
                onChange={handleFileSelect}
              />
            </Button>

            {file && (
              <Box sx={{ display: "flex", alignItems: "center", mr: 2 }}>
                <AttachFileIcon sx={{ mr: 0.5 }} />
                <Typography variant="body2">{file.name}</Typography>
                <IconButton size="small" onClick={handleFileRemove}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            )}

            {fileError && (
              <Typography variant="caption" color="error" sx={{ ml: 1 }}>
                {fileError}
              </Typography>
            )}
          </Box>

          <Box sx={{ mt: 2 }}>
            {showYouTube ? (
              <Box sx={{ display: "flex", gap: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="YouTube URL"
                  value={youTubeURL}
                  onChange={(e) => setYouTubeURL(e.target.value)}
                />
              </Box>
            ) : (
              <Button
                startIcon={<YouTubeIcon />}
                variant="outlined"
                disabled={!!file}
                onClick={() => setShowYouTube(true)}
              >
                Add YouTube
              </Button>
            )}
          </Box>
        </Box>

        <Typography variant="caption">
          Only upload .pdf, .jpg, .jpeg, .png, .mp4, .mov, .gif, .mp3, .doc,
          .docx
        </Typography>

        {/* CONTACT INFO — matches ContactDialog layout */}
        <Box sx={{ mt: 4, pt: 3, borderTop: "1px solid #e0e0e0" }}>
          <Typography fontWeight={700} fontSize="18px" mb={2}>
            Contact Information
          </Typography>

          <Grid container spacing={4}>
            {/* LEFT COLUMN */}
            <Grid item xs={12} md={6}>
              <Stack spacing={3}>
                {hasPersonalContact && (
                  <Box>
                    <Typography fontWeight={700} fontSize={18}>
                      PERSONAL CONTACT
                    </Typography>
                    <Stack spacing={1} mt={1}>
                      {mappedContact.phone && (
                        <Typography sx={{ wordBreak: "break-word" }}>
                          <strong>Primary Phone:</strong> +{mappedContact.phone}
                        </Typography>
                      )}

                      {mappedContact.secondary && (
                        <Typography sx={{ wordBreak: "break-word" }}>
                          <strong>Secondary Phone:</strong> +{mappedContact.secondary}
                        </Typography>
                      )}
                      {mappedContact.email && (
                        <Typography sx={{ wordBreak: "break-word" }}>
                          <strong>Email:</strong>{" "}
                          <Link href={`mailto:${mappedContact.email}`}>
                            {mappedContact.email}
                          </Link>
                        </Typography>
                      )}
                      {mappedContact.personalWebSite && (
                        <Typography sx={{ wordBreak: "break-word" }}>
                          <strong>Website:</strong>{" "}
                          <Link
                            href={normalizeUrl(mappedContact.personalWebSite)}
                            target="_blank"
                          >
                            {normalizeUrl(mappedContact.personalWebSite)}
                          </Link>
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                )}

                {repEntries.length > 0 && (
                  <Box>
                    <Typography fontWeight={700} fontSize={18}>
                      REPRESENTATION
                    </Typography>
                    <Stack spacing={2} mt={1}>
                      {headings.map((h) => {
                        const info = reps[h];
                        if (
                          !info ||
                          (!info.nameofCompany && !info.representativeContact)
                        )
                          return null;
                        return (
                          <Box key={h}>
                            <Typography fontWeight={600}>{h}</Typography>
                            {info.nameofCompany && (
                              <Typography>{info.nameofCompany}</Typography>
                            )}
                            {info.representativeName && (
                              <Typography>
                                Name: {info.representativeName}
                              </Typography>
                            )}
                            {info.representativeContact && (
                              <Typography>
                                Contact: +{info.representativeContact}
                              </Typography>
                            )}
                          </Box>
                        );
                      })}
                    </Stack>
                  </Box>
                )}
              </Stack>
            </Grid>

            {/* RIGHT COLUMN */}
            <Grid item xs={12} md={6}>
              <Stack spacing={3}>
                {paymentLinks.length > 0 && (
                  <Box>
                    <Typography fontWeight={700} fontSize={18}>
                      PAYMENT
                    </Typography>
                    <Stack spacing={1.5} mt={1}>
                      {paymentLinks.map((link, i) => (
                        <Stack
                          key={i}
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ flexWrap: "nowrap" }}
                        >
                          <AccountBalanceWalletIcon fontSize="small" />

                          <Link
                            href={normalizeUrl(link)}
                            target="_blank"
                            sx={{
                              display: "inline-block",
                              maxWidth: "220px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {normalizeUrl(link)}
                          </Link>
                        </Stack>
                      ))}
                    </Stack>
                  </Box>
                )}

                {socialItems.length > 0 && (
                  <Box>
                    <Typography fontWeight={700} fontSize={18} sx={{ mb: 1 }}>
                      FOLLOW
                    </Typography>
                    <Stack spacing={1.5}>
                      {socialItems.map((item, i) => (
                        <Stack
                          key={i}
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ flexWrap: "wrap" }}
                        >
                          {item.icon}
                          <Link
                            href={normalizeUrl(item.value)}
                            target="_blank"
                            sx={{
                              display: "inline-block",
                              maxWidth: "250px", // adjust based on layout
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {normalizeUrl(item.value)}
                          </Link>
                        </Stack>
                      ))}
                    </Stack>
                  </Box>
                )}
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          startIcon={<SendIcon />}
          onClick={handleSend}
          disabled={
            sending ||
            (isMessageEmpty(body) && !(showYouTube && youTubeURL.trim()))
          }
        >
          {sending ? "Sending…" : "Send"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
